import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { LogOut, Plus, Download, Edit, Trash2, FileText, Package, Mail, Settings as SettingsIcon, Loader2, Upload } from "lucide-react";
import { DIVISIONS, PACKAGING_OPTIONS } from "@/constants/site";

const STATUSES = ["New", "Contacted", "Quotation Sent", "Follow-up Required", "Converted", "Closed"];

export default function AdminDashboardPage() {
  const [tab, setTab] = useState("enquiries");
  const [me, setMe] = useState(null);
  const nav = useNavigate();

  useEffect(() => {
    api.get("/auth/me").then((r) => setMe(r.data)).catch(() => { localStorage.removeItem("ssk_token"); nav("/admin/login"); });
  }, []);

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch (_) { /* ignore */ }
    localStorage.removeItem("ssk_token");
    nav("/admin/login");
  };

  if (!me) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-slate-400" /></div>;

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-white border-b border-slate-200">
        <div className="container-x flex items-center justify-between h-16">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 bg-[#0F2747] text-white flex items-center justify-center font-display font-black text-sm">SSK</div>
            <div><div className="font-display font-bold text-sm">Admin Console</div><div className="text-xs text-slate-500">{me.email}</div></div>
          </div>
          <button onClick={logout} className="btn-outline text-sm" data-testid="admin-logout"><LogOut className="w-4 h-4" /> Logout</button>
        </div>
        <div className="container-x flex gap-1 border-t border-slate-100">
          {[
            { k: "enquiries", label: "Enquiries", icon: Mail },
            { k: "products", label: "Products", icon: Package },
            { k: "certificates", label: "Certificates", icon: FileText },
            { k: "settings", label: "Settings", icon: SettingsIcon },
          ].map((t) => (
            <button key={t.k} onClick={() => setTab(t.k)}
              className={`px-4 py-3 text-sm font-medium flex items-center gap-2 border-b-2 ${tab === t.k ? "border-[#0F2747] text-[#0F2747]" : "border-transparent text-slate-600 hover:text-slate-900"}`}
              data-testid={`admin-tab-${t.k}`}>
              <t.icon className="w-4 h-4" /> {t.label}
            </button>
          ))}
        </div>
      </header>
      <main className="container-x py-8">
        {tab === "enquiries" && <EnquiriesPanel />}
        {tab === "products" && <ProductsPanel />}
        {tab === "certificates" && <CertificatesPanel />}
        {tab === "settings" && <SettingsPanel />}
      </main>
    </div>
  );
}

function EnquiriesPanel() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("");
  const [editing, setEditing] = useState(null);

  const load = () => {
    setLoading(true);
    api.get("/admin/enquiries").then((r) => setRows(r.data)).finally(() => setLoading(false));
  };
  useEffect(load, []);

  const exportCsv = async () => {
    const backend = process.env.REACT_APP_BACKEND_URL;
    const t = localStorage.getItem("ssk_token");
    const res = await fetch(`${backend}/api/admin/enquiries/export.csv`, { headers: { Authorization: `Bearer ${t}` } });
    const blob = await res.blob();
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob); a.download = "enquiries.csv"; a.click();
  };

  const filtered = filter ? rows.filter((r) => r.status === filter) : rows;

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="font-display text-2xl font-bold">Enquiries</h2>
          <div className="text-sm text-slate-500 mt-1">{rows.length} total</div>
        </div>
        <div className="flex gap-2">
          <Select value={filter || "all"} onValueChange={(v) => setFilter(v === "all" ? "" : v)}>
            <SelectTrigger className="w-48" data-testid="enquiry-filter"><SelectValue placeholder="All statuses" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              {STATUSES.map((s) => (<SelectItem key={s} value={s}>{s}</SelectItem>))}
            </SelectContent>
          </Select>
          <button onClick={exportCsv} className="btn-outline" data-testid="export-csv"><Download className="w-4 h-4" /> Export CSV</button>
        </div>
      </div>

      <div className="bg-white border border-slate-200 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-left text-xs uppercase tracking-wider text-slate-600">
            <tr>
              <th className="px-4 py-3">Ref</th><th className="px-4 py-3">Date</th><th className="px-4 py-3">Name / Company</th>
              <th className="px-4 py-3">Product</th><th className="px-4 py-3">Status</th><th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={6} className="text-center py-10"><Loader2 className="w-6 h-6 animate-spin inline text-slate-400" /></td></tr>
            ) : filtered.length === 0 ? (
              <tr><td colSpan={6} className="text-center py-10 text-slate-500">No enquiries yet.</td></tr>
            ) : filtered.map((r) => (
              <tr key={r.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`enq-row-${r.id}`}>
                <td className="px-4 py-3 font-mono-tech text-xs">{r.reference}</td>
                <td className="px-4 py-3 text-slate-600">{new Date(r.created_at).toLocaleDateString()}</td>
                <td className="px-4 py-3"><div className="font-medium">{r.full_name}</div><div className="text-xs text-slate-500">{r.company_name || r.email}</div></td>
                <td className="px-4 py-3"><div>{r.product_name || "—"}</div><div className="text-xs text-slate-500">{r.division}</div></td>
                <td className="px-4 py-3"><StatusPill status={r.status} /></td>
                <td className="px-4 py-3 text-right"><button className="text-[#0F2747] font-medium text-xs" onClick={() => setEditing(r)} data-testid={`view-enq-${r.id}`}>View</button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {editing && <EnquiryDetail enquiry={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
    </div>
  );
}

function StatusPill({ status }) {
  const colors = { "New": "bg-blue-50 text-blue-700 border-blue-200", "Contacted": "bg-slate-100 text-slate-700 border-slate-200",
    "Quotation Sent": "bg-amber-50 text-amber-700 border-amber-200", "Follow-up Required": "bg-orange-50 text-orange-700 border-orange-200",
    "Converted": "bg-green-50 text-green-700 border-green-200", "Closed": "bg-slate-100 text-slate-600 border-slate-200" };
  return <span className={`text-xs px-2 py-1 border ${colors[status] || "bg-slate-50 border-slate-200"}`}>{status}</span>;
}

function EnquiryDetail({ enquiry, onClose, onSaved }) {
  const [status, setStatus] = useState(enquiry.status);
  const [notes, setNotes] = useState(enquiry.notes || "");
  const save = async () => {
    await api.patch(`/admin/enquiries/${enquiry.id}`, { status, notes });
    toast.success("Updated");
    onSaved();
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto rounded-none">
        <DialogHeader>
          <div className="overline">Enquiry {enquiry.reference}</div>
          <DialogTitle className="font-display">{enquiry.full_name} — {enquiry.company_name || ""}</DialogTitle>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3 text-sm">
          {[
            ["Phone", enquiry.phone], ["Email", enquiry.email], ["City / State", `${enquiry.city} / ${enquiry.state}`],
            ["Division", enquiry.division], ["Product", enquiry.product_name], ["Quantity", enquiry.quantity],
            ["Packaging", enquiry.packaging], ["Application", enquiry.application],
          ].map(([k, v]) => (
            <div key={k} className="border border-slate-200 p-3">
              <div className="overline">{k}</div><div className="text-slate-800 mt-1 break-words">{v || "—"}</div>
            </div>
          ))}
        </div>
        {enquiry.message && <div className="mt-3 border border-slate-200 p-3"><div className="overline">Message</div><div className="text-slate-800 mt-1 whitespace-pre-wrap">{enquiry.message}</div></div>}
        {enquiry.file_url && <a href={`${process.env.REACT_APP_BACKEND_URL}${enquiry.file_url}`} target="_blank" rel="noreferrer" className="text-[#0F2747] text-sm underline mt-2 inline-block">View attachment</a>}
        <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div><Label className="text-xs uppercase">Status</Label>
            <Select value={status} onValueChange={setStatus}>
              <SelectTrigger data-testid="enq-status"><SelectValue /></SelectTrigger>
              <SelectContent>{STATUSES.map((s) => (<SelectItem key={s} value={s}>{s}</SelectItem>))}</SelectContent>
            </Select>
          </div>
        </div>
        <Textarea placeholder="Follow-up notes" value={notes} onChange={(e) => setNotes(e.target.value)} rows={3} className="mt-3" data-testid="enq-notes" />
        <button onClick={save} className="btn-primary mt-4 w-full" data-testid="enq-save">Save Update</button>
      </DialogContent>
    </Dialog>
  );
}

function ProductsPanel() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const load = () => { setLoading(true); api.get("/admin/products").then((r) => setRows(r.data)).finally(() => setLoading(false)); };
  useEffect(load, []);
  const del = async (id) => { if (!window.confirm("Delete this product?")) return; await api.delete(`/admin/products/${id}`); toast.success("Deleted"); load(); };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h2 className="font-display text-2xl font-bold">Products</h2>
        <button onClick={() => setEditing({})} className="btn-primary" data-testid="add-product"><Plus className="w-4 h-4" /> Add Product</button>
      </div>
      <div className="bg-white border border-slate-200 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-left text-xs uppercase tracking-wider text-slate-600">
            <tr><th className="px-4 py-3">Name</th><th className="px-4 py-3">Division</th><th className="px-4 py-3">Category</th><th className="px-4 py-3">Active</th><th className="px-4 py-3"></th></tr>
          </thead>
          <tbody>
            {loading ? <tr><td colSpan={5} className="text-center py-8"><Loader2 className="w-5 h-5 animate-spin inline" /></td></tr> :
              rows.map((r) => (
                <tr key={r.id} className="border-t border-slate-100" data-testid={`prod-row-${r.id}`}>
                  <td className="px-4 py-3 font-medium">{r.name}</td>
                  <td className="px-4 py-3">{DIVISIONS[r.division]?.short || r.division}</td>
                  <td className="px-4 py-3">{r.category}</td>
                  <td className="px-4 py-3">{r.is_active ? "Yes" : "No"}</td>
                  <td className="px-4 py-3 text-right space-x-2">
                    <button onClick={() => setEditing(r)} className="text-slate-600 hover:text-[#0F2747]" data-testid={`edit-prod-${r.id}`}><Edit className="w-4 h-4 inline" /></button>
                    <button onClick={() => del(r.id)} className="text-slate-600 hover:text-red-600" data-testid={`del-prod-${r.id}`}><Trash2 className="w-4 h-4 inline" /></button>
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>
      {editing && <ProductEditor product={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
    </div>
  );
}

function ProductEditor({ product, onClose, onSaved }) {
  const [form, setForm] = useState({
    name: "", division: "ndt_materials", category: "", short_description: "", long_description: "",
    image_url: "", packaging: [], brand: "", bearing_type: "", is_liquid: true, is_active: true, display_order: 0,
    ...product,
  });
  const [uploading, setUploading] = useState(false);
  const change = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target ? e.target.value : e }));
  const togglePack = (p) => setForm((f) => ({ ...f, packaging: f.packaging.includes(p) ? f.packaging.filter((x) => x !== p) : [...f.packaging, p] }));
  const uploadImage = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/admin/upload", fd);
      setForm((f) => ({ ...f, image_url: `${process.env.REACT_APP_BACKEND_URL}${data.url}` }));
      toast.success("Image uploaded");
    } catch { toast.error("Upload failed"); } finally { setUploading(false); }
  };
  const save = async () => {
    try {
      if (product?.id) await api.put(`/admin/products/${product.id}`, form);
      else await api.post("/admin/products", form);
      toast.success("Saved"); onSaved();
    } catch (e) { toast.error("Save failed"); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto rounded-none">
        <DialogHeader><DialogTitle className="font-display">{product?.id ? "Edit Product" : "Add Product"}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <Input placeholder="Product name" value={form.name} onChange={change("name")} data-testid="pe-name" />
          <div className="grid grid-cols-2 gap-3">
            <Select value={form.division} onValueChange={change("division")}>
              <SelectTrigger data-testid="pe-division"><SelectValue /></SelectTrigger>
              <SelectContent>{Object.values(DIVISIONS).map((d) => (<SelectItem key={d.key} value={d.key}>{d.label}</SelectItem>))}</SelectContent>
            </Select>
            <Input placeholder="Category" value={form.category} onChange={change("category")} data-testid="pe-category" />
          </div>
          <Textarea placeholder="Short description" value={form.short_description} onChange={change("short_description")} rows={2} data-testid="pe-desc" />
          <div className="grid grid-cols-2 gap-3">
            <Input placeholder="Brand (e.g., KLYDE, NTN)" value={form.brand} onChange={change("brand")} />
            <Input placeholder="Bearing type (if applicable)" value={form.bearing_type} onChange={change("bearing_type")} />
          </div>
          <div>
            <Label className="text-xs uppercase">Applicable packaging (for liquid products)</Label>
            <div className="flex flex-wrap gap-2 mt-2">
              {PACKAGING_OPTIONS.filter((p) => p !== "Other").map((p) => (
                <button key={p} onClick={() => togglePack(p)} type="button"
                  className={`px-3 py-1.5 text-sm border ${form.packaging.includes(p) ? "bg-[#0F2747] text-white border-[#0F2747]" : "bg-white border-slate-300"}`}>
                  {p}
                </button>
              ))}
            </div>
          </div>
          <div>
            <Label className="text-xs uppercase">Product image</Label>
            <label className="flex items-center gap-3 cursor-pointer border border-dashed border-slate-300 px-4 py-3 bg-slate-50 mt-1">
              {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
              <span className="text-sm">{form.image_url ? "Replace image" : "Upload image"}</span>
              <input type="file" accept="image/*" className="hidden" onChange={uploadImage} />
            </label>
            {form.image_url && <img src={form.image_url} alt="preview" loading="lazy" decoding="async" className="mt-2 h-24 border border-slate-200" />}
          </div>
          <div className="flex gap-4 text-sm">
            <label className="flex items-center gap-2"><input type="checkbox" checked={form.is_liquid} onChange={(e) => setForm((f) => ({ ...f, is_liquid: e.target.checked }))} /> Liquid product</label>
            <label className="flex items-center gap-2"><input type="checkbox" checked={form.is_active} onChange={(e) => setForm((f) => ({ ...f, is_active: e.target.checked }))} /> Active</label>
          </div>
          <button onClick={save} className="btn-primary w-full" data-testid="pe-save">Save Product</button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function CertificatesPanel() {
  const [rows, setRows] = useState([]);
  const [editing, setEditing] = useState(null);
  const load = () => api.get("/certificates").then((r) => setRows(r.data));
  useEffect(() => { load(); }, []);
  const del = async (id) => { if (!window.confirm("Delete?")) return; await api.delete(`/admin/certificates/${id}`); load(); };
  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h2 className="font-display text-2xl font-bold">Certificates</h2>
        <button onClick={() => setEditing({})} className="btn-primary" data-testid="add-cert"><Plus className="w-4 h-4" /> Add Certificate</button>
      </div>
      <div className="bg-white border border-slate-200">
        {rows.map((r) => (
          <div key={r.id} className="p-4 border-b border-slate-100 flex items-center justify-between">
            <div>
              <div className="font-medium">{r.title}</div>
              <div className="text-xs text-slate-500 mt-0.5">
                <span className="inline-block px-1.5 py-0.5 bg-slate-100 border border-slate-200 mr-2">{r.document_type || "—"}</span>
                {r.category_slug || r.category} · {r.related_product}
                {r.file_url && <span className="ml-2 text-emerald-700">· hosted</span>}
                {!r.file_url && r.external_url && <span className="ml-2 text-slate-400">· external link</span>}
              </div>
            </div>
            <div className="flex gap-2">
              <button onClick={() => setEditing(r)} className="text-slate-600 hover:text-[#0F2747]"><Edit className="w-4 h-4" /></button>
              <button onClick={() => del(r.id)} className="text-slate-600 hover:text-red-600"><Trash2 className="w-4 h-4" /></button>
            </div>
          </div>
        ))}
      </div>
      {editing && <CertEditor cert={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
    </div>
  );
}

function CertEditor({ cert, onClose, onSaved }) {
  const [f, setF] = useState({
    title: "", document_type: "Brochure", category_slug: "other",
    related_product: "", category: "", external_url: "", file_url: "", image_url: "",
    is_featured: false, is_active: true, ...cert,
  });
  const save = async () => {
    try {
      if (cert?.id) await api.put(`/admin/certificates/${cert.id}`, f); else await api.post("/admin/certificates", f);
      toast.success("Saved"); onSaved();
    } catch { toast.error("Failed"); }
  };
  const uploadFile = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    try {
      const r = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setF({ ...f, file_url: r.data.url });
      toast.success("File uploaded");
    } catch { toast.error("Upload failed"); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-lg rounded-none max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>{cert?.id ? "Edit" : "Add"} Document</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <Input placeholder="Document title" value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} />
          <div className="grid grid-cols-2 gap-2">
            <select value={f.document_type} onChange={(e) => setF({ ...f, document_type: e.target.value })}
                    className="border border-slate-300 px-3 py-2 text-sm">
              <option value="Brochure">Brochure</option>
              <option value="Certificate">Certificate</option>
              <option value="Approval">Approval</option>
              <option value="Data Sheet">Data Sheet</option>
              <option value="CoA">Certificate of Analysis</option>
              <option value="Other">Other</option>
            </select>
            <select value={f.category_slug} onChange={(e) => setF({ ...f, category_slug: e.target.value })}
                    className="border border-slate-300 px-3 py-2 text-sm">
              <option value="authorisation">Authorisation</option>
              <option value="dpt">Dye Penetrant Testing</option>
              <option value="mpi">Magnetic Particle Inspection</option>
              <option value="uv">UV Technology</option>
              <option value="equipment">Equipment &amp; Accessories</option>
              <option value="klyde-zinc">KLYDE Zinc Materials</option>
              <option value="other">Other Documents</option>
            </select>
          </div>
          <Input placeholder="Related product / area" value={f.related_product} onChange={(e) => setF({ ...f, related_product: e.target.value })} />
          <Input placeholder="Official document URL (opens in new tab)" value={f.external_url} onChange={(e) => setF({ ...f, external_url: e.target.value })} />
          <div className="border border-dashed border-slate-300 p-3">
            <div className="text-xs text-slate-500 mb-2">Or host the PDF directly on this site:</div>
            <input type="file" accept="application/pdf,image/*" onChange={uploadFile} className="text-sm" />
            {f.file_url && <div className="text-xs text-emerald-700 mt-2 break-all">Uploaded: {f.file_url}</div>}
          </div>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={f.is_featured} onChange={(e) => setF({ ...f, is_featured: e.target.checked })} />
            Featured (SSK Authorisation Certificate)
          </label>
          <button onClick={save} className="btn-primary w-full">Save</button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function SettingsPanel() {
  const [f, setF] = useState(null);
  useEffect(() => { api.get("/settings").then((r) => setF(r.data)); }, []);
  if (!f) return <Loader2 className="w-6 h-6 animate-spin" />;
  const save = async () => { try { await api.put("/admin/settings", f); toast.success("Settings saved"); } catch { toast.error("Failed"); } };
  const ch = (k) => (e) => setF({ ...f, [k]: e.target.value });
  return (
    <div className="max-w-2xl">
      <h2 className="font-display text-2xl font-bold mb-6">Company Contact Settings</h2>
      <div className="space-y-3 bg-white p-6 border border-slate-200">
        <Field label="Business Name (must match Google Business Profile)"><Input value={f.business_name || ""} onChange={ch("business_name")} data-testid="settings-business-name" /></Field>
        <Field label="Office Phone / WhatsApp"><Input value={f.phone || ""} onChange={ch("phone")} /></Field>
        <Field label="Marketing Phone"><Input value={f.phone_marketing || ""} onChange={ch("phone_marketing")} /></Field>
        <Field label="Sales Phone"><Input value={f.phone_sales || ""} onChange={ch("phone_sales")} /></Field>
        <Field label="WhatsApp Number (e.g. +91XXXXXXXXXX)"><Input value={f.whatsapp || ""} onChange={ch("whatsapp")} /></Field>
        <Field label="Email"><Input value={f.email || ""} onChange={ch("email")} /></Field>
        <Field label="Full Address (must match Google Business Profile)"><Input value={f.address || ""} onChange={ch("address")} /></Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Street Address"><Input value={f.street_address || ""} onChange={ch("street_address")} /></Field>
          <Field label="Locality"><Input value={f.locality || ""} onChange={ch("locality")} /></Field>
          <Field label="Region / State"><Input value={f.region || ""} onChange={ch("region")} /></Field>
          <Field label="Postal Code"><Input value={f.postal_code || ""} onChange={ch("postal_code")} /></Field>
        </div>
        <Field label="GSTIN"><Input value={f.gst || ""} onChange={ch("gst")} /></Field>
        <Field label="LinkedIn URL"><Input value={f.linkedin || ""} onChange={ch("linkedin")} /></Field>
        <Field label="Google Business Profile URL"><Input value={f.gbp_url || ""} onChange={ch("gbp_url")} placeholder="https://maps.app.goo.gl/..." data-testid="settings-gbp-url" /></Field>
        <Field label="Google Maps Embed URL (iframe src) — optional"><Input value={f.map_embed || ""} onChange={ch("map_embed")} placeholder="https://www.google.com/maps/embed?pb=..." /></Field>
        <button onClick={save} className="btn-primary w-full mt-4" data-testid="settings-save">Save Settings</button>
      </div>
    </div>
  );
}
function Field({ label, children }) {
  return (<div className="space-y-1.5"><Label className="text-xs font-semibold uppercase tracking-wider text-slate-600">{label}</Label>{children}</div>);
}
